import { DateTime } from 'luxon';
import {
  useCallback, useEffect, useMemo, useRef, useState
} from 'react';
import { toast } from 'react-toastify';

import APIGateway from '@/api';
import { ChatConversationMessage } from '@/components/chatConversation/chatConversation';
import { AgentInvokeArgs, AgentInvokeStreamChunk } from '@/types/agent';
import {
  AgentInvokeApiReqUser,
  ChatMessageContentTextual,
  ChatMsgHistory,
  ChatMsgHistoryItem,
  ChatMsgTypeEnum,
  ChatRoleEnum,
  CompletionResponseTypeEnum
} from '@/types/chat';

import { DEFAULT_AGENT, ExecutedQuery, QueryResult } from '../types';
import { parseQueryLogs } from '../utils/queryLogParser';
import { useExplorerStreamingParser } from './useExplorerStreamingParser';
import { extractResultFromText } from './useQueryExecution';


type SessionContext = {
  userId?: string;
  [key: string]: unknown;
};

type UseAgentChatOptions = {
  sessionContext: SessionContext | null | undefined;
  sqlValue: string;
  updateResult: ( result: QueryResult ) => void;
  completeExecution: ( result: QueryResult | null ) => void;
  startExecution: () => void;
  setError: ( error: string ) => void;
  setSqlValue: ( sql: string ) => void;
  setActiveResultTab: ( tab: 'results' | 'queries' | 'statistics' ) => void;
};

type UseAgentChatReturn = {
  input: string;
  isWaiting: boolean;
  displayMessages: ChatConversationMessage[];
  queryLogs: ExecutedQuery[];
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
  onClickChat: ( mode?: string ) => Promise<void>;
  onInputChange: ( value: string ) => void;
  handleStop: () => void;
  handleExecuteSQL: ( sql?: string ) => Promise<void>;
};

export function useAgentChat( {
  sessionContext,
  sqlValue,
  updateResult,
  completeExecution,
  startExecution,
  setError,
  setSqlValue,
  setActiveResultTab
}: UseAgentChatOptions ): UseAgentChatReturn {
  // Chat state
  const [ sChatMsgHistory, setSChatMsgHistory ] = useState<ChatMsgHistory>( [] );
  const [ sChatUsers, setSChatUsers ] = useState<AgentInvokeApiReqUser[]>();
  const [ sCorrelationId, setSCorrelationId ] = useState<string | undefined>();
  const [ sInput, setSInput ] = useState( '' );
  const [ sIsWaiting, setSIsWaiting ] = useState( false );

  // Query log state
  const [ queryLogs, setQueryLogs ] = useState<ExecutedQuery[]>( [] );

  // Refs
  const abortControllerRef = useRef<AbortController | null>( null );
  const correlationIdRef = useRef<string | undefined>( undefined );
  const currentMessageIdRef = useRef<string | undefined>( undefined );
  const messagesEndRef = useRef<null | HTMLDivElement>( null );
  const inputRef = useRef( '' );
  const pendingQueryRef = useRef( false );
  const streamHandledRef = useRef( false );
  const hiddenMsgIdsRef = useRef<Set<string>>( new Set() );
  const isWaitingRef = useRef( false );

  // Accumulator for batched StructuredContent results (gRPC chunking)
  const batchAccRef = useRef<{ columns: string[]; rows: Record<string, unknown>[]; totalRows: number }>( { columns: [], rows: [], totalRows: 0 } );

  // Ref-sync effects
  useEffect( () => {
    inputRef.current = sInput;
  }, [ sInput ] );

  useEffect( () => {
    correlationIdRef.current = sCorrelationId;
  }, [ sCorrelationId ] );

  // Chat users setup
  useEffect( () => {
    if ( !sessionContext?.userId ) return;
    setSChatUsers( [
      { displayName: 'User', id: sessionContext.userId, type: ChatRoleEnum.USER },
      { displayName: DEFAULT_AGENT.displayName, id: DEFAULT_AGENT.name, type: ChatRoleEnum.ASSISTANT }
    ] );
  }, [ sessionContext ] );

  // Streaming parser
  const parseAgentChunk = useCallback( ( chunk: unknown ) => {
    const typedChunk = chunk as AgentInvokeStreamChunk;

    if ( typedChunk.correlation_id && !correlationIdRef.current ) {
      correlationIdRef.current = typedChunk.correlation_id;
      setSCorrelationId( typedChunk.correlation_id );
    }

    if ( typedChunk.type === CompletionResponseTypeEnum.AI_DATA_PLATFORM_ERROR ) {
      return { error: typedChunk.errorMessage || typedChunk.type, shouldUpdate: false };
    }

    if ( typedChunk.type === CompletionResponseTypeEnum.AI_DATA_PLATFORM_FAILURE ) {
      return { error: typedChunk.failureMessage || typedChunk.type, shouldUpdate: false };
    }

    if ( typedChunk.type === CompletionResponseTypeEnum.AI_DATA_PLATFORM_RESULT && typedChunk.messages && typedChunk.messages.length > 0 ) {
      const msg = typedChunk.messages[0];
      if ( !msg.content ) return { shouldUpdate: false };

      currentMessageIdRef.current = msg.message_id || typedChunk.response_id;
      return {
        content: '__FULL_REPLACE__' + JSON.stringify( msg.content ),
        shouldUpdate: true
      };
    }

    return { shouldUpdate: false };
  }, [] );

  const handleUpdateAgentMessage = useCallback( ( accumulatedContent: string ) => {
    const fullReplaceMarker = '__FULL_REPLACE__';
    const lastMarkerIndex = accumulatedContent.lastIndexOf( fullReplaceMarker );
    const contentJson = lastMarkerIndex >= 0
      ? accumulatedContent.substring( lastMarkerIndex + fullReplaceMarker.length )
      : accumulatedContent;

    let content: { type: ChatMsgTypeEnum } & Record<string, unknown>;
    try {
      content = JSON.parse( contentJson ) as { type: ChatMsgTypeEnum } & Record<string, unknown>;
    } catch( e ) {
      console.warn( 'Failed to parse agent message content:', e );
      return;
    }

    // Skip TypingAction messages
    if ( ( content as Record<string, unknown> ).typing !== undefined || content.type === ( 'TypingAction' as ChatMsgTypeEnum ) ) {
      return;
    }

    const incomingMessage: ChatMsgHistoryItem = {
      authorId: DEFAULT_AGENT.name,
      content,
      createdAt: DateTime.now(),
      messageId: currentMessageIdRef.current || crypto.randomUUID(),
      role: ChatRoleEnum.ASSISTANT,
      updatedAt: DateTime.now()
    };

    setSChatMsgHistory( prev => {
      const updated = [ ...prev ];
      let existingIdx = -1;
      for ( let i = prev.length - 1; i >= 0; i-- ) {
        if ( prev[i].messageId === incomingMessage.messageId ) {
          existingIdx = i;
          break;
        }
      }
      if ( existingIdx === -1 ) {
        updated.push( incomingMessage );
      } else {
        updated[existingIdx] = incomingMessage;
      }
      return updated;
    } );

    // Try to extract query results from the response text
    if ( content && 'type' in content ) {
      const typed = content as { type: string; text?: string; data?: unknown };

      if ( typed.type === ChatMsgTypeEnum.TEXTUAL_CONTENT && typed.text ) {
        // Extract SQL the agent ran and show it in the editor
        const sqlMatch = typed.text.match( /```sql\s*([\s\S]*?)```/ );
        if ( sqlMatch ) {
          setSqlValue( sqlMatch[1].trim() );
        }

        // Extract tabular results when a query is pending
        if ( pendingQueryRef.current ) {
          const result = extractResultFromText( typed.text );
          if ( result ) {
            completeExecution( result );
            pendingQueryRef.current = false;
            streamHandledRef.current = true;
            setActiveResultTab( 'results' );
          }
        }
      } else if ( typed.type === ChatMsgTypeEnum.STRUCTURED_CONTENT && typed.data ) {
        const data = typed.data as Record<string, unknown>;
        let rows: Record<string, unknown>[] | null = null;
        let columns: string[] | null = null;

        if ( data.query_result && Array.isArray( data.rows ) && ( data.rows as unknown[] ).length > 0 ) {
          rows = data.rows as Record<string, unknown>[];
          columns = Array.isArray( data.columns ) ? data.columns as string[] : Object.keys( rows[0] );
        } else if ( Array.isArray( data ) && ( data as unknown[] ).length > 0 ) {
          rows = data as unknown as Record<string, unknown>[];
          columns = Object.keys( rows[0] );
        } else if ( typeof data === 'object' && data !== null && !Array.isArray( data ) && !( 'query_result' in data ) ) {
          rows = [ data as Record<string, unknown> ];
          columns = Object.keys( rows[0] );
        }

        if ( rows && columns ) {
          const isBatched = typeof data.batch === 'number' && typeof data.total_rows === 'number';
          if ( isBatched ) {
            const acc = batchAccRef.current;
            if ( ( data.batch as number ) === 0 ) {
              acc.columns = columns;
              acc.rows = [ ...rows ];
              acc.totalRows = data.total_rows as number;
            } else {
              acc.rows = [ ...acc.rows, ...rows ];
            }
            // Switch to results tab on first batch
            if ( !streamHandledRef.current ) {
              pendingQueryRef.current = false;
              streamHandledRef.current = true;
              setActiveResultTab( 'results' );
            }
            const allReceived = acc.rows.length >= acc.totalRows;
            if ( allReceived ) {
              // Final batch — stop spinner and record execution time
              completeExecution( { columns: acc.columns, rows: acc.rows } );
              batchAccRef.current = { columns: [], rows: [], totalRows: 0 };
            } else {
              // Intermediate batch — update grid progressively, keep spinner running
              updateResult( { columns: acc.columns, rows: acc.rows } );
            }
          } else {
            completeExecution( { columns, rows } );
            pendingQueryRef.current = false;
            streamHandledRef.current = true;
            setActiveResultTab( 'results' );
          }
        }
      }
    }

    setTimeout( () => {
      messagesEndRef.current?.scrollIntoView( { behavior: 'smooth' } );
    }, 100 );
  }, [ completeExecution, updateResult, setSqlValue, setActiveResultTab ] );

  const finishPendingQuery = useCallback( ( action: 'error' | 'abort' | 'complete', message?: string ) => {
    // Always reset batch accumulator to prevent stale data leaking across queries
    batchAccRef.current = { columns: [], rows: [], totalRows: 0 };
    if ( streamHandledRef.current || !pendingQueryRef.current ) return;
    streamHandledRef.current = true;
    pendingQueryRef.current = false;
    if ( action === 'error' ) {
      setError( message ?? 'Unknown error' );
    } else if ( action === 'abort' ) {
      setError( 'Query aborted' );
    } else {
      completeExecution( null );
    }
  }, [ setError, completeExecution ] );

  const { processStream, abort } = useExplorerStreamingParser( {
    parseChunk: parseAgentChunk,
    onUpdateMessage: handleUpdateAgentMessage,
    onError: ( error ) => {
      console.error( error.message );
      toast.error( error.message );
      finishPendingQuery( 'error', error.message );
    },
    onAbort: () => {
      setSIsWaiting( false );
      finishPendingQuery( 'abort' );
    },
    onComplete: () => {
      finishPendingQuery( 'complete' );
    }
  } );

  const handleStop = useCallback( () => {
    if ( abortControllerRef.current ) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    abort();
  }, [ abort ] );

  // Cleanup on unmount
  useEffect( () => {
    return () => {
      if ( abortControllerRef.current ) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [] );

  // Load query logs from action/model logs after stream completes
  const loadQueryLogs = useCallback( async() => {
    const cid = correlationIdRef.current;
    if ( !cid ) return;

    try {
      const [ actionSettled, modelSettled ] = await Promise.allSettled( [
        APIGateway.logs.actionLogsGet( '', 50, 0, undefined, cid ),
        APIGateway.logs.modelLogsGet( '', 50, 0, undefined, cid )
      ] );
      const actionResult = actionSettled.status === 'fulfilled' ? actionSettled.value : { agent_actions: [] };
      const modelResult = modelSettled.status === 'fulfilled' ? modelSettled.value : { model_invocations: [] };

      const unique = parseQueryLogs( actionResult, modelResult );

      setQueryLogs( prev => {
        const prevSqls = new Set( prev.map( p => p.sql ) );
        const newEntries = unique.filter( q => !prevSqls.has( q.sql ) );
        return newEntries.length > 0 ? [ ...prev, ...newEntries ] : prev;
      } );
    } catch( e ) {
      console.error( 'Failed to load query logs:', e );
    }
  }, [] );

  const chatHistoryRef = useRef<ChatMsgHistory>( [] );
  useEffect( () => {
    chatHistoryRef.current = sChatMsgHistory;
  }, [ sChatMsgHistory ] );

  // Send a message to the agent
  const sendMessage = useCallback( async( text: string, options?: { hidden?: boolean } ) => {
    if ( !sessionContext || !sChatUsers || isWaitingRef.current ) return;

    isWaitingRef.current = true;
    setSIsWaiting( true );
    abortControllerRef.current = new AbortController();

    try {
      const msgId = crypto.randomUUID();
      if ( options?.hidden ) {
        hiddenMsgIdsRef.current.add( msgId );
      }

      const userMsg: ChatMsgHistoryItem<ChatMessageContentTextual> = {
        authorId: sessionContext.userId!,
        content: {
          type: ChatMsgTypeEnum.TEXTUAL_CONTENT,
          text
        },
        createdAt: DateTime.now(),
        messageId: msgId,
        role: ChatRoleEnum.USER,
        updatedAt: DateTime.now()
      };

      const newHistory: ChatMsgHistory = [ ...chatHistoryRef.current, userMsg ];
      setSChatMsgHistory( newHistory );
      setSInput( '' );

      setTimeout( () => {
        messagesEndRef.current?.scrollIntoView( { behavior: 'smooth' } );
      }, 250 );

      const typeCompatibleMessages = newHistory
        .filter( msg => ( msg.content as { type: string } ).type === ChatMsgTypeEnum.TEXTUAL_CONTENT )
        .slice( -20 );

      const args: AgentInvokeArgs = {
        clientId: 'aidp-studio',
        context: { values: [] },
        debug: true,
        messages: typeCompatibleMessages,
        agentId: DEFAULT_AGENT.name,
        users: sChatUsers
      };

      if ( correlationIdRef.current ) {
        args.correlationId = correlationIdRef.current;
      }

      const stream = await APIGateway.agents.agentInvokeAidpStream( args, abortControllerRef.current.signal );
      await processStream( stream );

      setTimeout( () => { loadQueryLogs().catch( () => {} ); }, 5000 );
    } catch( e ) {
      const error = e as Error;
      if ( error.name !== 'AbortError' ) {
        console.error( error.message );
        toast.error( error.message );
      }
    }

    isWaitingRef.current = false;
    setSIsWaiting( false );
    abortControllerRef.current = null;
  }, [ sessionContext, sChatUsers, processStream, loadQueryLogs ] );

  // Chat handlers
  const onClickChat = useCallback( async( _mode?: string ) => {
    if ( !inputRef.current.trim() ) return;
    await sendMessage( inputRef.current );
  }, [ sendMessage ] );

  const onInputChange = useCallback( ( value: string ) => {
    setSInput( value );
  }, [] );

  // SQL execution: send SQL directly to DuckDB cache (bypasses LLM)
  const handleExecuteSQL = useCallback( async( sql?: string ) => {
    const query = sql || sqlValue;
    if ( !query.trim() ) return;

    startExecution();
    pendingQueryRef.current = true;
    streamHandledRef.current = false;

    await sendMessage( `__DIRECT_SQL__:${ query }`, { hidden: true } );
  }, [ sqlValue, sendMessage, startExecution ] );

  // Map chat history to display messages
  const displayMessages = useMemo( () => sChatMsgHistory
    .filter( msg => {
      if ( hiddenMsgIdsRef.current.has( msg.messageId ) ) return false;
      const c = msg.content as Record<string, unknown>;
      if ( c.type === ChatMsgTypeEnum.STRUCTURED_CONTENT && c.data ) {
        const d = c.data as Record<string, unknown>;
        if ( d.query_result ) return false;
      }
      return true;
    } )
    .map( ( msg: ChatMsgHistoryItem ): ChatConversationMessage => {
      const content = msg.content as { type: string; text?: string };
      if ( content.type === ChatMsgTypeEnum.TEXTUAL_CONTENT ) {
        return { role: msg.role, text: content.text as string };
      }
      return { role: msg.role, json: msg.content as unknown as string };
    } ), [ sChatMsgHistory ]
  );

  return {
    input: sInput,
    isWaiting: sIsWaiting,
    displayMessages,
    queryLogs,
    messagesEndRef,
    onClickChat,
    onInputChange,
    handleStop,
    handleExecuteSQL
  };
}
